import {
  Stack,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Chip,
  FormGroup,
  Checkbox,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Divider } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../../../api/axios';
import WeeklyBars, { Row as WBRow } from '../../../../components/disponibilidad/WeeklyBars';
import { useCentros } from '../../../../hooks/useCentros';
import { useDisponibilidad, ReplaceFilaDto } from '../../../../hooks/useDisponibilidad';
import { usePivot } from '../../../../hooks/usePivots';
import { useProfesorFormatos } from '../../../../hooks/useProfesorFormatos';
import moralejaImg from '../../../../imagenes/Centros/moraleja.png';
import otherImg from '../../../../imagenes/Centros/other.png';
import plazacastillaImg from '../../../../imagenes/Centros/plazacastilla.png';
import pozueloImg from '../../../../imagenes/Centros/pozuelo.png';
import IconDelete from '../../../../imagenes/Iconos/Iconos_botones/icon_delete.png';
import IconEditar from '../../../../imagenes/Iconos/Iconos_botones/icon_reloj.png';

import { colorForCentro } from './colorForCentro';
import { WeeklyContextSnapshot } from './ProfesorDisponibilidadPage';

/* Iconos asignados para cada centro */
const centerIconMap: Record<number, string> = {
  1: moralejaImg,
  2: plazacastillaImg,
  3: pozueloImg,
  4: otherImg,
};

/* ═════════ bits formatos (UI) ═════════ */
const BIT_P = 1; // presencial
const BIT_O = 2; // online
const BIT_D = 4; // a domicilio
const STEP = 15;

function lightenHex(hex: string, ratio = 0.18) {
  const val = hex.replace('#', '');
  const full =
    val.length === 3
      ? val
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : val.padStart(6, '0');
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * ratio);
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

function buildLinearGradient(colors: string[]) {
  if (!colors.length) return '';
  if (colors.length === 1) return colors[0];
  const step = 100 / (colors.length - 1);
  const stops = colors.map((c, i) => `${c} ${Math.round(i * step)}%`).join(', ');
  return `linear-gradient(90deg, ${stops})`;
}

/**
 * Congela el color de UNA franja.
 * - Si la franja tiene idCentro => color sólido de ese centro en ese momento.
 * - Si la franja era "todos los centros" (idCentro === null) => degradado
 *   usando EXACTAMENTE los centros que existían en esa vigencia.
 */
function buildRowBgSnapshot(row: WBRow, candidateCenters: number[]): string {
  if (typeof row.idCentro === 'number') {
    return colorForCentro(row.idCentro);
  }
  const unique = Array.from(new Set(candidateCenters));
  if (unique.length === 0) return colorForCentro(null); // último fallback
  if (unique.length === 1) return colorForCentro(unique[0]);
  const hexes = unique.map((id) => lightenHex(colorForCentro(id)));
  return buildLinearGradient(hexes);
}

/** normaliza "HH:mm[:ss]" → "HH:mm" */
const hhmm = (s?: string | null) => (s ? String(s).slice(0, 5) : (s ?? ''));
const toMin = (hhmmStr: string) => {
  const [h, m] = hhmmStr.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};
const minToHHMM = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** mask numérica → array de strings requerido por el backend */
const maskToArray = (mask: number): Array<'presencial' | 'online' | 'a_domicilio'> => {
  const out: Array<'presencial' | 'online' | 'a_domicilio'> = [];
  if (mask & BIT_P) out.push('presencial');
  if (mask & BIT_O) out.push('online');
  if (mask & BIT_D) out.push('a_domicilio');
  return out.length ? out : (['presencial'] as any);
};

/** intenta obtener la máscara desde la fila */
const ensureMaskFromRow = (r: any): number => {
  const BIT_P2 = 1,
    BIT_O2 = 2,
    BIT_D2 = 4;
  if (Number.isFinite(Number(r?.formatosMask))) return Number(r.formatosMask);
  if (Array.isArray(r?.formatos)) {
    const set = new Set<string>(r.formatos.map(String));
    let m = 0;
    if (set.has('presencial')) m |= BIT_P2;
    if (set.has('online')) m |= BIT_O2;
    if (set.has('a_domicilio')) m |= BIT_D2;
    return m || BIT_P2;
  }
  return BIT_P2;
};

interface Props {
  profesorId: number;
  onRowsChange?: (rows: any[]) => void;
  onContextChange?: (ctx: WeeklyContextSnapshot) => void;
}

/* ═══════════════════ componente principal (Semanal) ═══════════════════ */
export default function DisponibilidadSemanal({
  profesorId,
  onRowsChange,
  onContextChange,
}: Props) {
  const { t } = useTranslation<'translation'>();
  const { rows: centros } = useCentros();
  const { refresh, saveAll, loading } = useDisponibilidad(profesorId ?? 0);
  const { assignedMask: formatsAssignedMask } = useProfesorFormatos(profesorId ?? 0);

  // Datos guardados (todas las vigencias)
  const [bloquesSemana, setBloquesSemana] = useState<any[]>([]);

  // Vigencia actualmente seleccionada para visualizar en la gráfica (modo view)
  const [selectedVigencia, setSelectedVigencia] = useState<{
    vd: string | null;
    vh: string | null;
  } | null>(null);

  // ====== Modo UI ======
  type Mode = 'view' | 'new' | 'edit';
  const [mode, setMode] = useState<Mode>('view');
  const editing = mode !== 'view';

  // Filtros/ámbito (oculto inicialmente)
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [allCentrosDinamico, setAllCentrosDinamico] = useState<boolean>(true);
  const [scopeCentros, setScopeCentros] = useState<number[]>([]);
  const [maskP, setMaskP] = useState(true);
  const [maskO, setMaskO] = useState(false);
  const [maskD, setMaskD] = useState(false);
  const scopeMask = useMemo(() => {
    const m = (maskP ? BIT_P : 0) | (maskO ? BIT_O : 0) | (maskD ? BIT_D : 0);
    return m || BIT_P;
  }, [maskP, maskO, maskD]);
  const [formatsExact, setFormatsExact] = useState<boolean>(false); // filtro laxo por defecto

  // Vigencia + comentario
  const [vigenciaDesde, setVigenciaDesde] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [finiteEnd, setFiniteEnd] = useState<boolean>(false);
  const [vigenciaHasta, setVigenciaHasta] = useState<string>(
    dayjs().add(6, 'month').format('YYYY-MM-DD'),
  );
  const [scopeComment, setScopeComment] = useState<string>('');

  // Borrados
  const [deletingVig, setDeletingVig] = useState(false);

  // Edición (lista de barras en WeeklyBars editable)
  const [draftRows, setDraftRows] = useState<WBRow[]>([]);

  // Scroll a las barras al entrar en edición
  const gridRef = useRef<HTMLDivElement | null>(null);
  const scrollToGrid = useCallback(() => {
    if (gridRef.current?.scrollIntoView) {
      try {
        gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        gridRef.current.scrollIntoView();
      }
    }
  }, []);

  // Centros asignados → degradado y leyenda (DEBE ir antes de withBgSnapshots)
  const pvCentros = usePivot('/profesores', profesorId ?? 0, 'centros');
  const assignedCenterIds = useMemo<number[]>(() => {
    const set = new Set<number>();
    for (const r of (pvCentros.rows ?? []) as any[]) {
      const id: any = r?.idCentro ?? r?.centro?.id ?? r?.id;
      if (typeof id === 'number') set.add(id);
    }
    return Array.from(set);
  }, [pvCentros.rows]);

  /**
   * Consolida filas duplicadas que tienen el mismo día, hora, formato y vigencia
   * pero diferentes centros, en una sola fila con idCentro = null
   */
  const consolidarFilasDuplicadas = useCallback((rows: any[]): any[] => {
    // Agrupar por: idProfesor, diaSemana, horaInicio, horaFin, formatosMask, vigenciaDesde, vigenciaHasta
    const grupos = new Map<string, any[]>();

    for (const r of rows ?? []) {
      // Normalizar horaInicio y horaFin para comparación (solo HH:mm)
      const horaInicio = String(r.horaInicio ?? '').slice(0, 5);
      const horaFin = String(r.horaFin ?? '').slice(0, 5);
      const key = `${r.idProfesor}|${r.diaSemana}|${horaInicio}|${horaFin}|${r.formatosMask ?? 7}|${r.vigenciaDesde ?? '1970-01-01'}|${r.vigenciaHasta ?? 'null'}`;
      if (!grupos.has(key)) grupos.set(key, []);
      grupos.get(key)!.push(r);
    }

    const consolidadas: any[] = [];

    for (const [key, grupo] of grupos.entries()) {
      if (grupo.length === 1) {
        // Solo una fila, mantenerla tal cual
        consolidadas.push(grupo[0]);
      } else {
        // Múltiples filas con el mismo día/hora/formato/vigencia pero diferentes centros
        // Consolidar en una sola fila con idCentro = null
        // Usar la primera fila como base, pero mantener el ID más bajo para referencia
        const primera = grupo[0];
        const ids = grupo
          .map((r) => Number(r.id))
          .filter(Number.isFinite)
          .sort((a, b) => a - b);
        const centrosEnGrupo = grupo
          .map((r) => r.idCentro)
          .filter((id): id is number => typeof id === 'number');
        const centrosUnicos = Array.from(new Set(centrosEnGrupo));

        // Si hay al menos un centro específico, consolidar; si todas son null, mantener null
        // Crear una fila consolidada
        consolidadas.push({
          ...primera,
          id: ids[0], // Usar el ID más bajo
          idCentro: null, // Consolidar como "todos los centros" (o los específicos si hay)
          // Almacenar los centros originales para el degradado y para poder guardarlos
          _centrosConsolidados: centrosUnicos.length > 0 ? centrosUnicos : undefined,
          // Almacenar todos los IDs originales para poder eliminarlos al guardar si es necesario
          _idsConsolidados: ids.length > 1 ? ids : undefined,
        });
      }
    }

    return consolidadas;
  }, []);

  const withBgSnapshots = useCallback(
    (rows: any[]) => {
      // Primero consolidar filas duplicadas
      const rowsConsolidadas = consolidarFilasDuplicadas(rows);

      const grupos: Record<string, { filas: any[]; centrosDeEsaVigencia: number[] }> = {};

      for (const r of rowsConsolidadas ?? []) {
        const vd = r.vigenciaDesde ?? '1970-01-01';
        const vh = r.vigenciaHasta ?? null;
        const key = `${vd}|${vh ?? ''}`;
        (grupos[key] ??= { filas: [], centrosDeEsaVigencia: [] }).filas.push(r);
        // Si tiene centros consolidados, usarlos; si no, usar idCentro
        if (r._centrosConsolidados && Array.isArray(r._centrosConsolidados)) {
          grupos[key].centrosDeEsaVigencia.push(...r._centrosConsolidados);
        } else if (typeof r.idCentro === 'number') {
          grupos[key].centrosDeEsaVigencia.push(r.idCentro);
        }
      }

      return Object.values(grupos).flatMap((g) => {
        const centrosUnicos = Array.from(new Set(g.centrosDeEsaVigencia)) as number[];
        const idsParaGradiente = centrosUnicos.length ? centrosUnicos : assignedCenterIds;
        return g.filas.map((r) => {
          // Determinar los centros para el degradado
          const centrosParaDegradado =
            r._centrosConsolidados &&
            Array.isArray(r._centrosConsolidados) &&
            r._centrosConsolidados.length > 0
              ? r._centrosConsolidados
              : r.idCentro != null
                ? [r.idCentro]
                : assignedCenterIds;

          return {
            ...r,
            bgSnapshot: buildRowBgSnapshot(
              {
                id: Number(r?.id) || -1,
                diaSemana: Number(r?.diaSemana ?? 0) as WBRow['diaSemana'],
                horaInicio: String(r?.horaInicio ?? '').slice(0, 5),
                horaFin: String(r?.horaFin ?? '').slice(0, 5),
                comentario: r?.comentario ?? null,
                idCentro: (r?.idCentro ?? null) as number | null,
                formatosMask: Number.isFinite(r?.formatosMask) ? Number(r.formatosMask) : undefined,
                vigenciaDesde: r?.vigenciaDesde ?? null,
                vigenciaHasta: r?.vigenciaHasta ?? null,
              } as WBRow,
              centrosParaDegradado,
            ),
          };
        });
      });
    },
    [assignedCenterIds, consolidarFilasDuplicadas],
  );

  // Cargar disponibilidad del profesor CON COLOR CONGELADO POR FILA
  useEffect(() => {
    if (!profesorId) return;
    let cancelled = false;
    (async () => {
      const rows = await refresh();
      if (cancelled) return;
      const snap = withBgSnapshots(rows ?? []);
      setBloquesSemana(snap);
      onRowsChange?.(snap);
    })();
    return () => {
      cancelled = true;
    };
  }, [profesorId, refresh, onRowsChange, withBgSnapshots]);

  // Exponer snapshot contexto (para la otra pestaña)
  useEffect(() => {
    onContextChange?.({
      // scopeCentro ya no aplica
      scopeMask,
      formatsExact,
      vigenciaDesde,
      finiteEnd,
      vigenciaHasta,
      // si quieres exponerlo:
      allCentrosDinamico,
      scopeCentros,
    } as any);
  }, [
    scopeMask,
    formatsExact,
    vigenciaDesde,
    finiteEnd,
    vigenciaHasta,
    allCentrosDinamico,
    scopeCentros,
    onContextChange,
  ]);

  // Mapa de centros
  const centrosMap = useMemo<Record<number, string>>(() => {
    const m: Record<number, string> = {};
    centros.forEach((c: any) => (m[c.id] = c.nombre));
    return m;
  }, [centros]);

  // Filas normalizadas (sólo lectura)
  const displayRows = useMemo<WBRow[]>(() => {
    const norm = (r: any, idx: number): WBRow => {
      // Si tiene centros consolidados, usarlos para _centrosArray
      const centrosArray =
        r._centrosConsolidados &&
        Array.isArray(r._centrosConsolidados) &&
        r._centrosConsolidados.length > 0
          ? r._centrosConsolidados
          : r._centrosArray && Array.isArray(r._centrosArray) && r._centrosArray.length > 0
            ? r._centrosArray
            : r.idCentro != null
              ? [r.idCentro]
              : undefined;

      return {
        id: Number.isFinite(r?.id) ? Number(r.id) : -100000 - idx,
        diaSemana: Number(r?.diaSemana ?? 0) as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        horaInicio: String(r?.horaInicio ?? '').slice(0, 5),
        horaFin: String(r?.horaFin ?? '').slice(0, 5),
        comentario: r?.comentario ?? null,
        idCentro: (r?.idCentro ?? null) as number | null,
        formatosMask: Number.isFinite(r?.formatosMask) ? Number(r.formatosMask) : undefined,
        vigenciaDesde: r?.vigenciaDesde ?? null,
        vigenciaHasta: r?.vigenciaHasta ?? null,
        bgSnapshot: r?.bgSnapshot,
        _centrosArray: centrosArray,
        // Preservar _centrosConsolidados para referencia
        _centrosConsolidados: r._centrosConsolidados,
      } as WBRow;
    };
    return (bloquesSemana ?? []).map((r, i) => norm(r, i));
  }, [bloquesSemana]);

  // Solo la vigencia base (vigenciaHasta === null)
  const baseRows = useMemo<WBRow[]>(() => {
    return displayRows.filter((r) => r.vigenciaHasta == null);
  }, [displayRows]);
  // Filas que se deben mostrar en la gráfica en modo "view"
  const viewedRows = useMemo<WBRow[]>(() => {
    // si el usuario ha hecho clic en una vigencia concreta:
    if (selectedVigencia) {
      const { vd, vh } = selectedVigencia;
      return displayRows.filter(
        (r) =>
          (r.vigenciaDesde ?? '1970-01-01') === (vd ?? '1970-01-01') &&
          (r.vigenciaHasta ?? null) === (vh ?? null),
      );
    }

    // si no hay selección explícita -> mostrar la BASE
    return baseRows;
  }, [selectedVigencia, displayRows, baseRows]);

  // Solo los centros asignados al profesor (por id)
  const centrosAsignados = useMemo(
    () => centros.filter((c: any) => assignedCenterIds.includes(c.id)),
    [centros, assignedCenterIds],
  );
  // Selección del usuario filtrada a los asignados
  const scopeCentrosAsignados = useMemo(
    () => scopeCentros.filter((id) => assignedCenterIds.includes(id)),
    [scopeCentros, assignedCenterIds],
  );

  // Si cambian los asignados, purga ids no válidos de la selección
  useEffect(() => {
    setScopeCentros((prev) => prev.filter((id) => assignedCenterIds.includes(id)));
  }, [assignedCenterIds]);
  const gradientColorsForAll = useMemo(() => {
    const orderedIds = assignedCenterIds?.length
      ? assignedCenterIds
      : Array.from(
          new Set(displayRows.filter((r) => r.idCentro != null).map((r) => r.idCentro as number)),
        );
    return orderedIds.map((id) => lightenHex(colorForCentro(id)));
  }, [assignedCenterIds, displayRows]);

  const allChipBackground = useMemo(() => {
    const valid = gradientColorsForAll.filter(Boolean);
    return valid.length >= 2 ? buildLinearGradient(valid) : colorForCentro(null);
  }, [gradientColorsForAll]);

  // Agrupar vigencias para panel
  const vigGroups = useMemo(() => {
    const m = new Map<string, { vd: string; vh: string | null; ids: number[] }>();
    for (const r of bloquesSemana) {
      const vd = r.vigenciaDesde ?? '1970-01-01';
      const vh = r.vigenciaHasta ?? null;
      const key = `${vd}|${vh ?? ''}`;
      if (!m.has(key)) m.set(key, { vd, vh, ids: [] });
      m.get(key)!.ids.push(r.id);
    }
    return Array.from(m.values()).sort((a, b) => a.vd.localeCompare(b.vd));
  }, [bloquesSemana]);

  // ids que deben formar el degradado AHORA MISMO
  const gradientIdsActuales = useMemo<number[]>(() => {
    // caso "dinámico": todos los centros asignados
    if (allCentrosDinamico) {
      return assignedCenterIds;
    }

    // caso manual: solo los centros que el usuario ha marcado en los chips
    if (scopeCentrosAsignados.length > 0) {
      return scopeCentrosAsignados;
    }

    // fallback: si no hay selección manual, que no pete
    return assignedCenterIds;
  }, [allCentrosDinamico, assignedCenterIds, scopeCentrosAsignados]);

  // ids que WeeklyBars debe usar para el degradado en ESTE render,
  // teniendo en cuenta si estamos creando una vigencia nueva o editando una existente.
  const gradientIdsParaWeeklyBars = useMemo<number[]>(() => {
    if (mode === 'new') {
      // Caso CREAR: queremos que el gradiente siga la selección actual del usuario
      // (dinámico / chips seleccionados).
      return gradientIdsActuales;
    }

    if (mode === 'edit') {
      // Caso EDITAR: queremos congelar los colores tal y como estaban.
      // Para eso miramos las filas que estamos editando (draftRows) y vemos
      // qué centros concretos aparecen en esa vigencia.
      const idsFromDraft = Array.from(
        new Set(
          draftRows.map((r) => r.idCentro).filter((id): id is number => typeof id === 'number'),
        ),
      );

      // Si esa vigencia ya tenía centros explícitos, usamos SOLO esos.
      if (idsFromDraft.length > 0) {
        return idsFromDraft;
      }

      // Si todas las franjas eran "dinámicas" (idCentro === null en todas),
      // no tenemos manera histórica de saber qué centros formaban el degradado original,
      // así que usamos el fallback más razonable: los centros asignados al profe.
      return assignedCenterIds;
    }

    // modo 'view' no llega aquí, pero por seguridad:
    return assignedCenterIds;
  }, [mode, gradientIdsActuales, draftRows, assignedCenterIds]);
  // ======= Acciones: Nueva / Editar / Eliminar vigencia =========
  //Checkboxes para selecionar modalidad
  const hasAssigned = (formatsAssignedMask & (BIT_P | BIT_O | BIT_D)) !== 0;
  const startNew = () => {
    setMode('new');
    setFiltersOpen(true);
    setDraftRows([]);
    setSelectedVigencia(null); // << limpiar selección visual
    scrollToGrid();
  };

  const handleEditVigencia = (vd: string, vh: string | null) => {
    setEditingSourcePeriod({ vd, vh });
    const listRaw = displayRows.filter(
      (r) => (r.vigenciaDesde ?? '1970-01-01') === vd && (r.vigenciaHasta ?? null) === vh,
    );

    // 2. Qué centros reales salen en esa vigencia (para el gradiente de filas sin centro fijo)
    // Considerar idCentro, _centrosArray y _centrosConsolidados
    const centrosIds = new Set<number>();
    for (const r of listRaw) {
      if (r.idCentro != null) centrosIds.add(r.idCentro);
      if ((r as any)._centrosArray && Array.isArray((r as any)._centrosArray)) {
        (r as any)._centrosArray.forEach((id: number) => centrosIds.add(id));
      }
      if ((r as any)._centrosConsolidados && Array.isArray((r as any)._centrosConsolidados)) {
        (r as any)._centrosConsolidados.forEach((id: number) => centrosIds.add(id));
      }
    }
    const centrosDeEsaVigencia = Array.from(centrosIds);

    // 3. Congelar color de cada fila en bgSnapshot
    const listWithSnapshot = listRaw.map((row) => ({
      ...row,
      bgSnapshot: row.bgSnapshot ?? buildRowBgSnapshot(row, centrosDeEsaVigencia),
    }));

    // 4. Seteamos el estado
    setVigenciaDesde(vd);
    setFiniteEnd(vh != null);
    if (vh) setVigenciaHasta(vh);
    setDraftRows(listWithSnapshot);
    setMode('edit');
    setFiltersOpen(true);
    scrollToGrid();
  };

  const deleteVigencia = async (vd: string, vh: string | null) => {
    const ids = bloquesSemana
      .filter((r) => (r.vigenciaDesde ?? '1970-01-01') === vd && (r.vigenciaHasta ?? null) === vh)
      .map((r) => r.id);
    if (!ids.length) return;
    if (
      !window.confirm(
        `Se van a eliminar ${ids.length} fila(s) de la vigencia ${vd} → ${vh ?? '∞'}. ¿Continuar?`,
      )
    )
      return;
    setDeletingVig(true);
    try {
      await Promise.all(ids.map((id) => api.delete(`/disponibilidad-profesor/${id}`)));
      const rows = await refresh();
      const snap = withBgSnapshots(rows ?? []);
      setBloquesSemana(snap);
      onRowsChange?.(snap);
      setSnack({ open: true, msg: 'Vigencia eliminada' });
    } catch {
      setSnack({ open: true, msg: 'Error al eliminar la vigencia', error: true });
    } finally {
      setDeletingVig(false);
    }
  };

  const cancelEdit = () => {
    setMode('view');
    setFiltersOpen(false);
    setDraftRows([]);
    setEditingSourcePeriod(null);
    // volver a vista base al salir de editar
    setSelectedVigencia(null);
  };
  // ======= Guardar =======

  const samePeriod = useCallback(
    (r: any) => {
      const vhTarget = finiteEnd ? vigenciaHasta : null;
      return (
        (r.vigenciaDesde ?? '1970-01-01') === vigenciaDesde &&
        (r.vigenciaHasta ?? null) === vhTarget
      );
    },
    [vigenciaDesde, vigenciaHasta, finiteEnd],
  );

  const normalizeFilaForReplace = (r: WBRow, profesorId: number): ReplaceFilaDto => {
    const mask = ensureMaskFromRow(r);
    const dto: ReplaceFilaDto = {
      idProfesor: profesorId,
      diaSemana: r.diaSemana,
      horaInicio: hhmm(r.horaInicio),
      horaFin: hhmm(r.horaFin),
      comentario: (r.comentario ?? '').trim() || undefined,
      formatos: maskToArray(mask),
    };
    const centrosEditadosExplicitamente = (r as any)._centrosTouched === true;

    if (centrosEditadosExplicitamente) {
      if (
        (r as any)._centrosArray &&
        Array.isArray((r as any)._centrosArray) &&
        (r as any)._centrosArray.length > 0
      ) {
        dto.centros = (r as any)._centrosArray;
      } else if (r.idCentro != null) {
        dto.centros = [r.idCentro];
      }
      return dto;
    }

    // Prioridad: _centrosArray (editado) > _centrosConsolidados (cargado) > idCentro > null
    // Si tiene array de centros almacenado temporalmente (múltiples centros), usarlo
    if (
      (r as any)._centrosArray &&
      Array.isArray((r as any)._centrosArray) &&
      (r as any)._centrosArray.length > 0
    ) {
      dto.centros = (r as any)._centrosArray;
    } else if (
      (r as any)._centrosConsolidados &&
      Array.isArray((r as any)._centrosConsolidados) &&
      (r as any)._centrosConsolidados.length > 0
    ) {
      // Si tiene centros consolidados (de filas duplicadas), usarlos
      dto.centros = (r as any)._centrosConsolidados;
    } else if (r.idCentro != null) {
      // Un solo centro
      dto.centros = [r.idCentro];
    }
    // Si no hay centros, no se incluye el campo (será null = todos los centros)
    return dto;
  };

  const saveNew = async () => {
    const scopeBase: ReplaceFilaDto[] = draftRows.map((r) => {
      const centrosEditadosExplicitamente = (r as any)._centrosTouched === true;

      const centrosForRow = centrosEditadosExplicitamente
        ? (r as any)._centrosArray &&
          Array.isArray((r as any)._centrosArray) &&
          (r as any)._centrosArray.length > 0
          ? (r as any)._centrosArray
          : r.idCentro != null
            ? [r.idCentro]
            : undefined
        : r.idCentro != null
          ? [r.idCentro]
          : allCentrosDinamico || scopeCentros.length === 0
            ? undefined
            : scopeCentros;

      const formatos = maskToArray(scopeMask);

      return {
        idProfesor: profesorId,
        diaSemana: r.diaSemana,
        horaInicio: hhmm(r.horaInicio),
        horaFin: hhmm(r.horaFin),
        comentario: scopeComment?.trim() || undefined,
        formatos,
        ...(centrosForRow ? { centros: centrosForRow } : {}),
      } as ReplaceFilaDto;
    });

    const matchFormat = (rMask: number) =>
      formatsExact
        ? Number(rMask ?? BIT_P) === scopeMask
        : (Number(rMask ?? BIT_P) & scopeMask) !== 0;

    const centerMatchesScope = (idCentro?: number | null) => {
      if (allCentrosDinamico) return true;
      if (!scopeCentros.length) return false;
      return idCentro != null && scopeCentros.includes(idCentro);
    };

    const others = bloquesSemana
      .filter((r) => samePeriod(r))
      .filter(
        (r) => !(centerMatchesScope(r.idCentro ?? null) && matchFormat(r.formatosMask ?? BIT_P)),
      );

    const keep: ReplaceFilaDto[] = others.map((row) =>
      normalizeFilaForReplace(
        {
          id: row.id,
          diaSemana: row.diaSemana,
          horaInicio: row.horaInicio.slice(0, 5),
          horaFin: row.horaFin.slice(0, 5),
          comentario: row.comentario ?? null,
          idCentro: row.idCentro ?? null,
          _centrosArray: (row as any)._centrosArray,
          _centrosConsolidados: (row as any)._centrosConsolidados,
          _centrosTouched: (row as any)._centrosTouched,
          formatosMask: row.formatosMask,
          vigenciaDesde: row.vigenciaDesde ?? null,
          vigenciaHasta: row.vigenciaHasta ?? null,
        },
        profesorId,
      ),
    );

    const payload = [...keep, ...scopeBase];
    await saveAll(payload, vigenciaDesde, finiteEnd ? vigenciaHasta : undefined);
  };

  const saveEdit = async () => {
    const payload: ReplaceFilaDto[] = draftRows.map((r) => normalizeFilaForReplace(r, profesorId));

    const newVh = finiteEnd ? vigenciaHasta : null;

    // 1) Reemplazar de forma atómica usando el periodo origen cuando se editan fechas
    await saveAll(
      payload,
      vigenciaDesde,
      newVh ?? undefined,
      editingSourcePeriod?.vd,
      editingSourcePeriod?.vh ?? null,
    );
  };

  const [snack, setSnack] = useState<{ open: boolean; msg: string; error?: boolean }>({
    open: false,
    msg: '',
  });
  // Guardamos la pareja de fechas original cuando entramos en "editar"
  const [editingSourcePeriod, setEditingSourcePeriod] = useState<{
    vd: string;
    vh: string | null;
  } | null>(null);
  // Loader local mientras se guarda
  const [saving, setSaving] = useState(false);
  const doSave = async () => {
    setSaving(true);
    try {
      if (mode === 'new') await saveNew();
      else if (mode === 'edit') await saveEdit();
      setSnack({ open: true, msg: t('availability.snackbar.ok', { defaultValue: 'Guardado' }) });
      const rows = await refresh();
      const snap = withBgSnapshots(rows ?? []);
      setBloquesSemana(snap);
      onRowsChange?.(snap);
      cancelEdit();
    } catch (e) {
      setSnack({
        open: true,
        msg: t('availability.snackbar.error', { defaultValue: 'Error al guardar' }),
        error: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // ======= Quick Add (doble‑clic) =======
  const nextIdRef = useRef(-200000);
  const [qaOpen, setQaOpen] = useState(false);
  const [qaDays, setQaDays] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);
  const [qaStart, setQaStart] = useState<string>('09:00');
  const [qaEnd, setQaEnd] = useState<string>('10:00');

  // ======= Edición individual de barra =======
  const [editBarOpen, setEditBarOpen] = useState(false);
  const [editingBar, setEditingBar] = useState<WBRow | null>(null);
  const [editBarHoraInicio, setEditBarHoraInicio] = useState<string>('09:00');
  const [editBarHoraFin, setEditBarHoraFin] = useState<string>('10:00');
  const [editBarFormatos, setEditBarFormatos] = useState<{
    presencial: boolean;
    online: boolean;
    a_domicilio: boolean;
  }>({ presencial: true, online: false, a_domicilio: false });
  const [editBarCentros, setEditBarCentros] = useState<number[] | 'all'>('all');
  const [editBarComentario, setEditBarComentario] = useState<string>('');

  const openQuickAdd = (day: WBRow['diaSemana'], approxMinute: number) => {
    const start = Math.max(0, Math.min(1440 - STEP, Math.floor(approxMinute / STEP) * STEP));
    const end = Math.min(1440, start + 60);
    const arr = [false, false, false, false, false, false, false];
    arr[day] = true;
    setQaDays(arr);
    setQaStart(minToHHMM(start));
    setQaEnd(minToHHMM(end));
    setQaOpen(true);
  };

  const commitQuickAdd = () => {
    const s = toMin(qaStart);
    const e = toMin(qaEnd);
    if (!(e > s)) return;

    const defaults: Partial<WBRow> = {
      // Centro inicial para las nuevas franjas
      idCentro: allCentrosDinamico
        ? null
        : scopeCentrosAsignados.length === 1
          ? scopeCentrosAsignados[0] // si has seleccionado exactamente un centro (ej. Chamartín)
          : null, // si hay más de uno marcado o modo dinámico, lo dejamos vacío

      // Modalidad inicial (presencial / online / etc.)
      formatosMask: scopeMask,
    };

    const created: WBRow[] = [];
    qaDays.forEach((checked, idx) => {
      if (!checked) return;
      created.push({
        id: nextIdRef.current--,
        diaSemana: idx as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        horaInicio: hhmm(minToHHMM(s)),
        horaFin: hhmm(minToHHMM(e)),
        comentario: null,
        ...defaults,
      } as WBRow);
    });
    if (created.length) {
      setDraftRows((prev) => [
        ...prev.map((r) => ({
          ...r,
          bgSnapshot: r.bgSnapshot ?? colorForCentro(r.idCentro ?? null),
        })),
        ...created.map((r) => ({
          ...r,
          bgSnapshot: colorForCentro(r.idCentro ?? null),
        })),
      ]);
    }
    setQaOpen(false);
  };

  // ======= Edición individual de barra =======
  const openEditBar = (row: WBRow) => {
    if (!editing) return;
    setEditingBar(row);
    setEditBarHoraInicio(hhmm(row.horaInicio));
    setEditBarHoraFin(hhmm(row.horaFin));
    setEditBarComentario(row.comentario ?? '');

    // Inicializar formatos
    const mask = ensureMaskFromRow(row);
    setEditBarFormatos({
      presencial: (mask & BIT_P) !== 0,
      online: (mask & BIT_O) !== 0,
      a_domicilio: (mask & BIT_D) !== 0,
    });

    // Inicializar centros
    // Prioridad: _centrosArray (editado) > _centrosConsolidados (cargado consolidado) > idCentro > 'all'
    if (
      (row as any)._centrosArray &&
      Array.isArray((row as any)._centrosArray) &&
      (row as any)._centrosArray.length > 0
    ) {
      setEditBarCentros((row as any)._centrosArray);
    } else if (
      (row as any)._centrosConsolidados &&
      Array.isArray((row as any)._centrosConsolidados) &&
      (row as any)._centrosConsolidados.length > 0
    ) {
      setEditBarCentros((row as any)._centrosConsolidados);
    } else if (row.idCentro != null) {
      setEditBarCentros([row.idCentro]);
    } else {
      setEditBarCentros('all');
    }
    setEditBarOpen(true);
  };

  const commitEditBar = () => {
    if (!editingBar) return;

    const s = toMin(editBarHoraInicio);
    const e = toMin(editBarHoraFin);
    if (!(e > s)) return;

    // Calcular máscara de formatos
    const newMask =
      (editBarFormatos.presencial ? BIT_P : 0) |
      (editBarFormatos.online ? BIT_O : 0) |
      (editBarFormatos.a_domicilio ? BIT_D : 0);
    if (newMask === 0) {
      // Al menos uno debe estar seleccionado
      setSnack({ open: true, msg: 'Debe seleccionar al menos un formato', error: true });
      return;
    }

    // Determinar centros
    // Si es 'all' o array vacío, usar null (todos los centros)
    // Si hay un solo centro, usar ese idCentro
    // Si hay múltiples centros, usar null pero almacenar el array para el guardado
    const newCentros = editBarCentros === 'all' ? [] : editBarCentros;
    const newCentro =
      newCentros.length === 0 ? null : newCentros.length === 1 ? newCentros[0] : null;

    // Actualizar la barra en draftRows
    setDraftRows((prev) =>
      prev.map((r) =>
        r.id === editingBar.id
          ? {
              ...r,
              horaInicio: hhmm(minToHHMM(s)),
              horaFin: hhmm(minToHHMM(e)),
              formatosMask: newMask,
              idCentro: newCentro,
              // Almacenar el array completo en un campo temporal para cuando se guarde
              // Si hay múltiples centros, guardar el array; si es 'all' o un solo centro, no guardar nada especial
              ...(newCentros.length > 1
                ? {
                    _centrosArray: newCentros,
                    _centrosConsolidados: undefined,
                    _centrosTouched: true,
                  }
                : {
                    _centrosArray: undefined,
                    _centrosConsolidados: undefined,
                    _centrosTouched: true,
                  }),
              comentario: editBarComentario.trim() || null,
              // Actualizar bgSnapshot según los centros
              bgSnapshot: (() => {
                if (newCentros.length === 0) {
                  // Todos los centros - degradado con todos los asignados
                  const ids = gradientIdsParaWeeklyBars;
                  if (ids.length >= 2) {
                    const hexes = ids.map((id) => lightenHex(colorForCentro(id)));
                    return buildLinearGradient(hexes);
                  }
                  if (ids.length === 1) {
                    return lightenHex(colorForCentro(ids[0]));
                  }
                  return colorForCentro(null);
                } else if (newCentros.length === 1) {
                  // Un solo centro - color sólido
                  return colorForCentro(newCentros[0]);
                } else {
                  // Múltiples centros - degradado con esos centros específicos
                  const hexes = newCentros.map((id) => lightenHex(colorForCentro(id)));
                  return buildLinearGradient(hexes);
                }
              })(),
            }
          : r,
      ),
    );

    setEditBarOpen(false);
    setEditingBar(null);
  };

  // ======= UI =======
  const currentScopeMask = (maskP ? BIT_P : 0) | (maskO ? BIT_O : 0) | (maskD ? BIT_D : 0);
  const assigned = formatsAssignedMask || 0;
  const allAssignedSelected =
    assigned > 0
      ? (currentScopeMask & assigned) === assigned && (currentScopeMask & ~assigned) === 0
      : currentScopeMask === (BIT_P | BIT_O | BIT_D);
  const anyAssignedSelected =
    assigned > 0 ? (currentScopeMask & assigned) !== 0 : currentScopeMask !== 0;

  return (
    <>
      {/* Barra de acciones */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="flex-start"
        sx={{ mb: 1 }}
      >
        {mode === 'view' && (
          <Button
            variant="outlined"
            onClick={startNew}
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#21A598',
              borderColor: '#21A598',
              '&:hover': { backgroundColor: '#ade8e86c', borderColor: '#21A598' },
            }}
          >
            {t('disponibilidad.nueva_vigencia')}
          </Button>
        )}
      </Stack>

      {/* Filtros/ámbito — ocultos inicialmente. Se muestran en Nueva/Editar */}
      {filtersOpen && (
        <Stack spacing={2} direction="column" alignItems="stretch" sx={{ mb: 2 }}>
          {/* CENTROS como chips (misma estética que WeeklyBars) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('disponibilidad.centros')}:
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {/* Chip "Todos los centros asignados (dinámico)" */}
              <Chip
                label={t('disponibilidad.todos_los_centros')}
                size="small"
                clickable
                onClick={() => {
                  setAllCentrosDinamico(true);
                  setScopeCentros([]);
                }}
                sx={{
                  ...(allCentrosDinamico
                    ? {
                        background: allChipBackground,
                        color: 'black',
                        border: '1px solid rgba(119,119,119,0.6)',
                        boxShadow: 1,
                      }
                    : {
                        bgcolor: 'transparent',
                        border: '1px dashed rgba(119,119,119,0.6)',
                        color: 'text.primary',
                      }),
                  fontWeight: 700,
                }}
                // ⛔️ quitar disabled completamente
              />

              {centrosAsignados.map((c: any) => {
                const selected = scopeCentros.includes(c.id) && !allCentrosDinamico;
                const bg = colorForCentro(c.id);

                return (
                  <Chip
                    key={c.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="img"
                          src={centerIconMap[c.id] ?? otherImg}
                          alt=""
                          sx={{ width: 18, height: 18, objectFit: 'contain' }}
                        />
                        {c.nombre}
                      </Box>
                    }
                    size="small"
                    clickable
                    onClick={() => {
                      setAllCentrosDinamico(false);
                      setScopeCentros((prev) =>
                        selected ? prev.filter((id) => id !== c.id) : [...prev, c.id],
                      );
                    }}
                    sx={{
                      ...(selected
                        ? {
                            bgcolor: bg,
                            color: 'black',
                            border: '1px solid rgba(119,119,119,0.6)',
                            boxShadow: 1,
                            pl: '6px',
                          }
                        : {
                            bgcolor: 'transparent',
                            border: '1px dashed rgba(119,119,119,0.6)',
                            color: 'text.primary',
                            pl: '6px',
                          }),
                      '& .MuiChip-icon': { width: 'auto', height: 20, mr: 0.5 },
                      fontWeight: 700,
                    }}
                    // ⛔️ quitar disabled completamente
                  />
                );
              })}
            </Stack>
          </Box>
          {hasAssigned && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('disponibilidad.modalidades')}:
              </Typography>

              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allAssignedSelected}
                      indeterminate={anyAssignedSelected && !allAssignedSelected}
                      onChange={(_, v) => {
                        if (v) {
                          // marcar todas las modalidades que el profe tiene asignadas
                          setMaskP((formatsAssignedMask & BIT_P) !== 0);
                          setMaskO((formatsAssignedMask & BIT_O) !== 0);
                          setMaskD((formatsAssignedMask & BIT_D) !== 0);
                        } else {
                          // volver a mínimo válido: presencial sí, online y domicilio no
                          setMaskP((formatsAssignedMask & BIT_P) !== 0);
                          setMaskO(false);
                          setMaskD(false);
                        }
                      }}
                      sx={{
                        color: '#21A598 !important',
                        '&.Mui-checked': { color: '#21A598 !important' },
                      }}
                    />
                  }
                  label={t('disponibilidad.todos_los_centros')}
                />

                {(formatsAssignedMask & BIT_P) !== 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={maskP}
                        onChange={(_, v) => setMaskP(v)}
                        sx={{
                          color: '#21A598 !important',
                          '&.Mui-checked': { color: '#21A598 !important' },
                        }}
                      />
                    }
                    label={t('modalidad.presencial')}
                  />
                )}

                {(formatsAssignedMask & BIT_O) !== 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={maskO}
                        onChange={(_, v) => setMaskO(v)}
                        sx={{
                          color: '#21A598 !important',
                          '&.Mui-checked': { color: '#21A598 !important' },
                        }}
                      />
                    }
                    label="Online"
                  />
                )}

                {(formatsAssignedMask & BIT_D) !== 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={maskD}
                        onChange={(_, v) => setMaskD(v)}
                        sx={{
                          color: '#21A598 !important',
                          '&.Mui-checked': { color: '#21A598 !important' },
                        }}
                      />
                    }
                    label={t('modalidad.a_domicilio')}
                  />
                )}
              </FormGroup>
            </Box>
          )}
          <Divider sx={{ width: '100%', borderColor: '#21A598', mt: 1, mb: 1 }} />

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              type="date"
              label={t('disponibilidad.vigencia_desde')}
              InputLabelProps={{ shrink: true }}
              value={vigenciaDesde}
              onChange={(e) => setVigenciaDesde(e.target.value)}
              sx={{
                minWidth: 190,
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#21A598',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#21A598',
                },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={finiteEnd}
                  onChange={(_, v) => setFiniteEnd(v)}
                  sx={{
                    // Cuando está activado
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#FFFFFF',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#21A598 !important',
                    },

                    // 🎯 Cuando está desactivado
                    '& .MuiSwitch-track': {
                      backgroundColor: 'rgba(166, 167, 166, 0.35)',
                      opacity: 1,
                    },
                    '& .MuiSwitch-thumb': {
                      color: '#21A598',
                      border: '2px solid rgba(85, 85, 85, 1)ff',
                    },

                    // Hover también en estado desactivado
                    '&:hover .MuiSwitch-track': {
                      backgroundColor: 'rgba(33,165,152,0.5)',
                    },
                  }}
                />
              }
              label={t('disponibilidad.con_fecha_fin')}
              sx={{ whiteSpace: 'nowrap' }}
            />

            <TextField
              type="date"
              label={t('disponibilidad.vigencia_hasta')}
              InputLabelProps={{ shrink: true }}
              value={vigenciaHasta}
              onChange={(e) => setVigenciaHasta(e.target.value)}
              sx={{
                minWidth: 190,
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#21A598',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#21A598',
                },
              }}
              disabled={!finiteEnd}
            />
          </Stack>

          {/* Comentario permanece en su propia línea */}
          <TextField
            label={t('disponibilidad.concepto_vigencia')}
            value={scopeComment}
            onChange={(e) => setScopeComment(e.target.value)}
            sx={{
              minWidth: 260,
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#21A598',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#21A598',
              },
            }}
            disabled={false}
          />
        </Stack>
      )}

      {/* Barras semanales */}
      <Box sx={{ mt: 2 }} ref={gridRef}>
        {editing ? (
          <WeeklyBars
            rows={[]}
            value={draftRows}
            onChange={setDraftRows}
            editable
            centrosMap={centrosMap}
            titulo={
              mode === 'new'
                ? (t('disponibilidad.titulo_nueva_vigencia') as string)
                : (t('disponibilidad.titulo_editar_vigencia') as string)
            }
            fullMaskForAll={formatsAssignedMask || 7}
            assignedCenterIds={assignedCenterIds}
            stepMinutes={STEP}
            newRowDefaults={{
              idCentro: allCentrosDinamico
                ? null
                : scopeCentrosAsignados.length === 1
                  ? scopeCentrosAsignados[0]
                  : null,
              formatosMask: scopeMask,
            }}
            timeLayout={[
              {
                fromMin: 0,
                toMin: 1440,
                columnsPerHour: 1 as const,
                hourLabel: (h: number) => `${String(h).padStart(2, '0')}:00`,
              },
            ]}
            onEmptyDoubleClick={(day, minute) => openQuickAdd(day, minute)}
            onRowDoubleClick={editing ? openEditBar : undefined}
            gradientCenterIdsForAll={gradientIdsParaWeeklyBars}
          />
        ) : (
          <WeeklyBars
            rows={viewedRows}
            centrosMap={centrosMap}
            titulo={t('disponibilidad.titulo_grafica_disponibilidad') as string}
            mode="time"
            fullMaskForAll={formatsAssignedMask || 7}
            assignedCenterIds={assignedCenterIds}
            timeLayout={[
              {
                fromMin: 0,
                toMin: 1440,
                columnsPerHour: 1 as const,
                hourLabel: (h: number) => `${String(h).padStart(2, '0')}:00`,
              },
            ]}
          />
        )}
      </Box>
      {editing && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ mt: 2, mb: 2 }}
        >
          <Button
            variant="contained"
            onClick={doSave}
            disabled={loading || saving}
            sx={{
              backgroundColor: '#21A598',
              '&:hover': { backgroundColor: '#1c8f84' },
            }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('disponibilidad.guardar_cambios')
            )}
          </Button>

          <Button variant="text" onClick={cancelEdit}>
            {t('disponibilidad.cancelar')}
          </Button>
        </Stack>
      )}

      {/* Panel de vigencias guardadas */}
      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          p: 2,
          borderColor: '#21A598',
          borderWidth: 2,
          borderStyle: 'solid',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1, fontFamily: '"Motiva Sans", sans-serif', color: '#21A598' }}
        >
          {t('disponibilidad.vigencias_guardadas')}
        </Typography>

        {vigGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {vigGroups.map((g) => {
              const isSelected =
                !editing &&
                // caso 1: hay selección explícita
                ((selectedVigencia &&
                  selectedVigencia.vd === g.vd &&
                  selectedVigencia.vh === g.vh) ||
                  // caso 2: no hay selección -> estamos mostrando la base
                  (!selectedVigencia && g.vh == null));

              return (
                <Paper
                  key={`${g.vd}|${g.vh ?? ''}`}
                  elevation={0}
                  onClick={() => {
                    if (editing) {
                      // si estamos editando, NO cambiamos la vista, porque ahí ya estás modificando otra cosa
                      return;
                    }
                    // al hacer click, fijamos esa vigencia para la vista
                    setSelectedVigencia({ vd: g.vd, vh: g.vh ?? null });
                    // también podríamos hacer scroll a la gráfica si quieres:
                    if (gridRef.current?.scrollIntoView) {
                      try {
                        gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } catch {
                        gridRef.current.scrollIntoView();
                      }
                    }
                  }}
                  sx={{
                    p: 1,
                    pr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    backgroundColor: '#FFFFFF',
                    border: '1.5px solid #21A598',
                    borderRadius: 1,
                    width: { xs: '100%', sm: '40%' },
                    cursor: editing ? 'default' : 'pointer',
                    ...(isSelected && !editing
                      ? {
                          borderColor: '#1c8f84',
                          boxShadow: '0 0 0 4px rgba(33,165,152,0.9)',
                          backgroundColor: 'rgba(33,165,152,0.05)',
                        }
                      : {}),
                    '&:hover': !editing
                      ? {
                          borderColor: '#1c8f84',
                          backgroundColor: 'rgba(33,165,152,0.08)',
                        }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                    }}
                  >
                    {g.vh == null && (
                      <Chip
                        size="small"
                        color="primary"
                        label="BASE"
                        sx={{
                          mr: 0.5,
                          minWidth: 96,
                          px: 0,
                          bgcolor: '#21A598',
                          color: '#FFFFFF',
                          '& .MuiChip-label': {
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            textAlign: 'center',
                          },
                        }}
                      />
                    )}
                    <Chip label={`${g.vd} → ${g.vh ?? '∞'}`} sx={{ bgcolor: 'grey.100' }} />
                  </Box>

                  {/* Botón editar (sigue funcionando igual) */}
                  <IconButton
                    aria-label="Editar vigencia"
                    onClick={(e) => {
                      e.stopPropagation(); // que no dispare el onClick del Paper
                      handleEditVigencia(g.vd, g.vh);
                    }}
                    sx={{
                      border: '1.5px solid #21A598',
                      borderRadius: 1,
                      p: 0.75,
                      '&:hover': { borderColor: '#1c8f84', bgcolor: 'rgba(33,165,152,0.08)' },
                    }}
                  >
                    <Box
                      component="img"
                      src={IconEditar}
                      alt="Editar"
                      sx={{ width: 18, height: 18, display: 'block' }}
                    />
                  </IconButton>

                  {/* Botón eliminar (sigue igual) */}
                  <IconButton
                    aria-label="Eliminar vigencia"
                    onClick={(e) => {
                      e.stopPropagation(); // que no dispare selección de vista
                      deleteVigencia(g.vd, g.vh);
                    }}
                    disabled={deletingVig}
                    sx={{
                      border: '1.5px solid #D90000',
                      borderRadius: 1.5,
                      p: 0.75,
                      ml: 1,
                      '&:hover': { borderColor: '#b30000', bgcolor: 'rgba(217,0,0,0.08)' },
                      '&.Mui-disabled': { opacity: 0.5, borderColor: '#D90000' },
                    }}
                  >
                    <Box
                      component="img"
                      src={IconDelete}
                      alt="Eliminar"
                      sx={{ width: 18, height: 18, display: 'block' }}
                    />
                  </IconButton>
                </Paper>
              );
            })}
          </Stack>
        )}

        <Box
          sx={{
            mt: 1.25,
            width: { xs: '100%', sm: '40%' },
            borderRadius: 2,
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={startNew}
            sx={{
              width: '50%',
              backgroundColor: '#FFFFFF',
              color: '#21A598',
              borderColor: '#21A598',
              '&:hover': { backgroundColor: '#ade8e86c', borderColor: '#21A598' },
            }}
          >
            {t('disponibilidad.nueva_vigencia')}
          </Button>
        </Box>
      </Paper>

      {/* Diálogo de “añadir rápido” por doble‑clic — MÁS GRANDE y sin texto introductorio */}
      <Dialog
        open={qaOpen}
        onClose={() => setQaOpen(false)}
        fullWidth
        maxWidth="sm" // ← más ancho que 'xs'
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: '2px solid #21A598',
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
            minWidth: 620, // ← asegura que no aparezca scroll horizontal
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 2,
            py: 1.25,
            bgcolor: '#21A598',
            color: '#FFFFFF',
            fontFamily: '"Motiva Sans", sans-serif',
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          Añadir franjas
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1, overflowX: 'hidden' }}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((lbl, idx) => (
                <FormControlLabel
                  key={lbl}
                  control={
                    <Checkbox
                      checked={qaDays[idx]}
                      onChange={(_, v) => {
                        const c = qaDays.slice();
                        c[idx] = v;
                        setQaDays(c);
                      }}
                      sx={{ color: '#21A598', '&.Mui-checked': { color: '#21A598' } }}
                    />
                  }
                  label={lbl}
                  sx={{
                    m: 0,
                    px: 0.75,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: '"Motiva Sans", sans-serif',
                      fontWeight: 600,
                      color: '#000',
                    },
                  }}
                />
              ))}
            </Stack>

            <Stack direction="row" spacing={2.5} justifyContent="center">
              <TextField
                type="time"
                label="Hora inicio"
                value={qaStart}
                onChange={(e) => setQaStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: STEP * 60 }}
                sx={{
                  minWidth: 200,
                  '& .MuiInputLabel-root.Mui-focused': { color: '#21A598' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#21A598',
                  },
                }}
              />
              <TextField
                type="time"
                label="Hora fin"
                value={qaEnd}
                onChange={(e) => setQaEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: STEP * 60 }}
                sx={{
                  minWidth: 200,
                  '& .MuiInputLabel-root.Mui-focused': { color: '#21A598' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#21A598',
                  },
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button onClick={() => setQaOpen(false)} sx={{ color: '#21A598' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={commitQuickAdd}
            disabled={toMin(qaEnd) <= toMin(qaStart) || !qaDays.some(Boolean)}
            sx={{
              backgroundColor: '#21A598',
              '&:hover': { backgroundColor: '#1c8f84' },
              fontWeight: 600,
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de edición individual de barra */}
      <Dialog
        open={editBarOpen}
        onClose={() => {
          setEditBarOpen(false);
          setEditingBar(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: '2px solid #21A598',
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
            minWidth: 620,
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 2,
            py: 1.25,
            bgcolor: '#21A598',
            color: '#FFFFFF',
            fontFamily: '"Motiva Sans", sans-serif',
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          Editar franja
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1, overflowX: 'hidden' }}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {/* Horarios */}
            <Stack direction="row" spacing={2.5} justifyContent="center">
              <TextField
                type="time"
                label="Hora inicio"
                value={editBarHoraInicio}
                onChange={(e) => setEditBarHoraInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: STEP * 60 }}
                sx={{
                  minWidth: 200,
                  '& .MuiInputLabel-root.Mui-focused': { color: '#21A598' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#21A598',
                  },
                }}
              />
              <TextField
                type="time"
                label="Hora fin"
                value={editBarHoraFin}
                onChange={(e) => setEditBarHoraFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: STEP * 60 }}
                sx={{
                  minWidth: 200,
                  '& .MuiInputLabel-root.Mui-focused': { color: '#21A598' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#21A598',
                  },
                }}
              />
            </Stack>

            {/* Formatos */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('disponibilidad.modalidades')}:
              </Typography>
              <FormGroup row>
                {(formatsAssignedMask & BIT_P) !== 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editBarFormatos.presencial}
                        onChange={(_, v) =>
                          setEditBarFormatos((prev) => ({ ...prev, presencial: v }))
                        }
                        sx={{
                          color: '#21A598 !important',
                          '&.Mui-checked': { color: '#21A598 !important' },
                        }}
                      />
                    }
                    label={t('modalidad.presencial')}
                  />
                )}
                {(formatsAssignedMask & BIT_O) !== 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editBarFormatos.online}
                        onChange={(_, v) => setEditBarFormatos((prev) => ({ ...prev, online: v }))}
                        sx={{
                          color: '#21A598 !important',
                          '&.Mui-checked': { color: '#21A598 !important' },
                        }}
                      />
                    }
                    label="Online"
                  />
                )}
                {(formatsAssignedMask & BIT_D) !== 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editBarFormatos.a_domicilio}
                        onChange={(_, v) =>
                          setEditBarFormatos((prev) => ({ ...prev, a_domicilio: v }))
                        }
                        sx={{
                          color: '#21A598 !important',
                          '&.Mui-checked': { color: '#21A598 !important' },
                        }}
                      />
                    }
                    label={t('modalidad.a_domicilio')}
                  />
                )}
              </FormGroup>
            </Box>

            {/* Centros */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('disponibilidad.centros')}:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  label={t('disponibilidad.todos_los_centros')}
                  size="small"
                  clickable
                  onClick={() => setEditBarCentros('all')}
                  sx={{
                    ...(editBarCentros === 'all'
                      ? {
                          background: allChipBackground,
                          color: 'black',
                          border: '1px solid rgba(119,119,119,0.6)',
                          boxShadow: 1,
                        }
                      : {
                          bgcolor: 'transparent',
                          border: '1px dashed rgba(119,119,119,0.6)',
                          color: 'text.primary',
                        }),
                    fontWeight: 700,
                  }}
                />
                {centrosAsignados.map((c: any) => {
                  const selected = editBarCentros !== 'all' && editBarCentros.includes(c.id);
                  const bg = colorForCentro(c.id);
                  return (
                    <Chip
                      key={c.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            component="img"
                            src={centerIconMap[c.id] ?? otherImg}
                            alt=""
                            sx={{ width: 18, height: 18, objectFit: 'contain' }}
                          />
                          {c.nombre}
                        </Box>
                      }
                      size="small"
                      clickable
                      onClick={() => {
                        if (editBarCentros === 'all') {
                          setEditBarCentros([c.id]);
                        } else if (editBarCentros.includes(c.id)) {
                          // Deseleccionar
                          const nuevos = editBarCentros.filter((id) => id !== c.id);
                          setEditBarCentros(nuevos.length === 0 ? 'all' : nuevos);
                        } else {
                          // Seleccionar (añadir)
                          setEditBarCentros([...editBarCentros, c.id]);
                        }
                      }}
                      sx={{
                        ...(selected
                          ? {
                              bgcolor: bg,
                              color: 'black',
                              border: '1px solid rgba(119,119,119,0.6)',
                              boxShadow: 1,
                              pl: '6px',
                            }
                          : {
                              bgcolor: 'transparent',
                              border: '1px dashed rgba(119,119,119,0.6)',
                              color: 'text.primary',
                              pl: '6px',
                            }),
                        '& .MuiChip-icon': { width: 'auto', height: 20, mr: 0.5 },
                        fontWeight: 700,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            {/* Comentario */}
            <TextField
              label={t('disponibilidad.concepto_vigencia')}
              value={editBarComentario}
              onChange={(e) => setEditBarComentario(e.target.value)}
              fullWidth
              sx={{
                '& .MuiInputLabel-root.Mui-focused': { color: '#21A598' },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#21A598',
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button
            onClick={() => {
              setEditBarOpen(false);
              setEditingBar(null);
            }}
            sx={{ color: '#21A598' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={commitEditBar}
            disabled={toMin(editBarHoraFin) <= toMin(editBarHoraInicio)}
            sx={{
              backgroundColor: '#21A598',
              '&:hover': { backgroundColor: '#1c8f84' },
              fontWeight: 600,
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.msg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={snack.error ? { sx: { bgcolor: 'error.main' } } : undefined}
      />
    </>
  );
}
